import isEqual from "lodash/isEqual";
import {
    decrementCertificateCountAPICompleted,
    incrementCertificateCountAPICompleted,
    initialState,
    reloadCertificateStatuses,
    setFormState,
    setIsLoadingCertificateCount,
    setLoading,
    setNoDataType,
    setNoSearch,
    setPage,
    setPageToNull,
} from ".";
import axios from "../../axios";
import { buildMultiTaxRequestParams } from "../../features/search/Filters/mapMultiTaxToApiParams";
import {
    customerRelationshipOptions,
    getCustomerRelationShip,
    processIdentifiers,
} from "../../features/search/Filters/utility";
import toast from "../../hooks/toast";
import { CertificateStatuses, completeRequestStatuses } from "../../shared/constants";
import { deduplicateRequest } from "../../shared/services/apiDeduplicationService";
import {
    buildApiV3Url,
    fixSearchStringForCertificates,
    lowerCaseAllExceptFirst,
    transformExemptionData,
} from "../../shared/Utils";
import { getStoredItem } from "../../shared/Utils.js";
import { setCertificateIds } from "../bulkUpdateSlice";

const [showToast] = toast();

// Module-level promises to track in-flight requests and prevent duplicate concurrent API calls.
// certificateSearch/CountRequestKey (a serialized request body) drives the reuse-in-flight-promise
// dedup optimization; it's fine for that to be recycled/reset once a request settles. Staleness
// (should THIS response be applied to the page) is instead decided by a monotonically increasing
// sequence number, which is never reset - unlike the request key, it can't alias two different
// requests that happen to share an identical body once the key has been recycled in between.
let certificateSearchPromise = null;
let certificateSearchRequestKey = null;
let certificateSearchSequence = 0;
let certificateCountPromise = null;
let certificateCountRequestKey = null;
let certificateCountSequence = 0;

// Helper functions
const isFiltersEmpty = filtersState => {
    const filtersWithoutSavedSearch = { ...filtersState };
    delete filtersWithoutSavedSearch.savedSearch;
    delete filtersWithoutSavedSearch.isCustomerDetails;
    delete filtersWithoutSavedSearch.isCertificateDetails;
    const initialStateWithoutSavedSearch = { ...initialState.certificateFiltersState };
    delete initialStateWithoutSavedSearch.savedSearch;
    delete initialStateWithoutSavedSearch.isCustomerDetails;
    delete initialStateWithoutSavedSearch.isCertificateDetails;

    return isEqual(filtersWithoutSavedSearch, initialStateWithoutSavedSearch);
};

const getExemptionStatuses = (certificateFiltersState, statuses) => {
    const exemptionStatuses = [];
    const hasValidStatus = completeRequestStatuses.some(status =>
        certificateFiltersState?.certificateStatus?.includes(status)
    );
    if (hasValidStatus) {
        const exemptionStatus = statuses?.filter(
            status =>
                status?.name === CertificateStatuses.Complete ||
                status?.name === CertificateStatuses.PENDING_FUTURE
        );
        exemptionStatus?.forEach(ele => exemptionStatuses.push(ele?.id));
    }
    if (certificateFiltersState?.certificateStatus?.includes(CertificateStatuses.PendingFuture)) {
        exemptionStatuses.push(
            statuses.filter(status => status?.name === CertificateStatuses.PENDING_FUTURE)[0]?.id
        );
    }
    if (
        certificateFiltersState?.certificateStatus?.includes(
            lowerCaseAllExceptFirst(CertificateStatuses.Pending)
        )
    ) {
        exemptionStatuses.push(
            statuses.filter(status => status?.name === CertificateStatuses.Pending)[0]?.id
        );
    }

    return exemptionStatuses;
};

const getSearchVal = (formState, certificateFiltersState) => {
    if (formState.nameOrCodeWhileLoading !== "") return formState.nameOrCodeWhileLoading;
    if (certificateFiltersState?.nameOrCode !== "") return certificateFiltersState.nameOrCode;
    return formState.nameOrCode;
};

// A missing/empty date filter is sent as null rather than "".
const dateOrNull = value => value || null;

const createRequestBody = ({
    searchVal,
    certificateFiltersState,
    pageSize,
    pageNumber,
    sortColumn,
    exemptionStatuses,
    searchType,
    taxTypeDetails,
}) => {
    return {
        pageSize,
        pageNumber,
        orderBy: sortColumn[0],
        orderByDirection: sortColumn[1] ? "ASC" : "DESC",
        nameOrCode: searchVal ?? "",
        purchaseOrderNumber: certificateFiltersState.invoiceNumber,
        isPrimary: certificateFiltersState.isCertificateActive,
        returnEmptyEmailRecords: certificateFiltersState.isMissingEmailAddress,
        isBillTo: getCustomerRelationShip(
            certificateFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isBillTo
        ),
        isShipTo: getCustomerRelationShip(
            certificateFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isShipTo
        ),
        isDuplicate: certificateFiltersState.isDuplicateRelation,
        isSingle: certificateFiltersState.isPurchaseOrder,
        certificateImageExists: certificateFiltersState.isCertificateImage,
        customerCreatedFrom: dateOrNull(certificateFiltersState.customerCreatedDateAfter),
        customerCreatedTo: dateOrNull(certificateFiltersState.customerCreatedDateBefore),
        customerModifiedFrom: dateOrNull(certificateFiltersState.customerModifiedDateAfter),
        customerModifiedTo: dateOrNull(certificateFiltersState.customerModifiedDateBefore),
        certificateCreatedFrom: dateOrNull(certificateFiltersState.certificateCreatedDateAfter),
        certificateCreatedTo: dateOrNull(certificateFiltersState.certificateCreatedDateBefore),
        certificateModifiedFrom: dateOrNull(certificateFiltersState.certificateModifiedDateAfter),
        certificateModifiedTo: dateOrNull(certificateFiltersState.certificateModifiedDateBefore),
        certificateEffectiveFrom: dateOrNull(certificateFiltersState.certificateEffectiveDateAfter),
        certificateEffectiveTo: dateOrNull(certificateFiltersState.certificateEffectiveDateBefore),
        certificateExpirationFrom: dateOrNull(
            certificateFiltersState.certificateExpirationDateAfter
        ),
        certificateExpirationTo: dateOrNull(
            certificateFiltersState.certificateExpirationDateBefore
        ),
        certificateValidatedFrom: dateOrNull(certificateFiltersState.certificateValidatedDateAfter),
        certificateValidatedTo: dateOrNull(certificateFiltersState.certificateValidatedDateBefore),
        status: certificateFiltersState.certificateStatus,
        exemptionStatus: exemptionStatuses,
        exposureZones: certificateFiltersState.certificateRegions,
        exemptionReasons: certificateFiltersState.exemptReasons,
        invalidReasons: certificateFiltersState.invalidReasons,
        customerRegions: certificateFiltersState.customerRegions,
        productExemptionCategories: certificateFiltersState.productExemptionCategories,
        certificateIds: processIdentifiers(certificateFiltersState.certificateIds, value =>
            Number(fixSearchStringForCertificates(value))
        ).filter(id => id !== 0 && Number.isFinite(id)),
        customerCodes: processIdentifiers(
            certificateFiltersState.customerCodes,
            fixSearchStringForCertificates
        ),
        activeCertificate: certificateFiltersState.isCertActive,
        certificateHasCustomers: certificateFiltersState.certificateHasCustomers,
        certificateCustomFields: certificateFiltersState.certificateCustomFields,
        customerCustomFields: certificateFiltersState.customerCustomFields,
        certificatesLabels: certificateFiltersState.certificatesLabels,
        excludeCertificateLabels: certificateFiltersState.excludeCertificateLabels,
        customersLabels: certificateFiltersState.customersLabels,
        reviewedBy: certificateFiltersState.reviewedBy,
        companyDivisionId: certificateFiltersState.companyGroup.companyDivisionId,
        companyIds: certificateFiltersState.companyGroup.companyIds,
        excludeCustomerLabels: certificateFiltersState.excludeCustomerLabels,
        searchType,
        customerExposures: certificateFiltersState.customerExposures,
        // Multi-tax params: deduped certificateJurisdictionCodes (SUT + non-SUT, ANDed with
        // taxSubTaxMappingIds by the API) and the flat taxSubTaxMappingIds list.
        ...buildMultiTaxRequestParams(certificateFiltersState, taxTypeDetails),
    };
};

// Loading State Handling
const handleLoadingState = (dispatch, formState, searchVal, skipLoadingCheck) => {
    dispatch(setLoading(true));
    if (formState.isloading && !skipLoadingCheck) {
        dispatch(
            setFormState({
                ...formState,
                nameOrCodeWhileLoading: searchVal,
            })
        );
        return true; // Skip API call
    }
    return false;
};

// API Call & Response Handling
const fetchCertificatesData = async (requestBody, dispatch, onLanding, pageSize) => {
    try {
        const apiResponse = await axios.post(buildApiV3Url(`certificates/search`), requestBody, {
            withCredentials: true,
        });

        if (!apiResponse || !apiResponse.data) {
            dispatch(setNoDataType(onLanding ? "onLanding" : "onSearch"));
            dispatch(setPageToNull(pageSize));
            dispatch(setLoading(false));
            return null;
        } else {
            dispatch(setNoDataType(""));
        }

        return apiResponse;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching certificates:", error);
        dispatch(setLoading(false));
        return null;
    }
};

// Pagination Handling
const handlePagination = (
    apiResponse,
    pageData,
    dispatch,
    pageSize,
    pageNumber,
    paginateDetails,
    response
) => {
    const paginateData = paginateDetails
        ? {
              pageNumber,
              startIndex: paginateDetails.startIndex,
              endIndex: paginateDetails.endIndex,
              rowsPerPage: paginateDetails.rowsPerPage,
              totalRecords: pageData.totalRecords,
              indeterminate: false,
          }
        : {
              pageNumber,
              startIndex: 0,
              endIndex: 19,
              rowsPerPage: pageSize,
              totalRecords:
                  response?.isCertificateCountAPICompleted === 0 ? pageData.totalRecords : 20,
              indeterminate: response?.isCertificateCountAPICompleted !== 0,
          };

    dispatch(
        setPage({
            data: response.data.value,
            paginate: paginateData,
        })
    );
};

// Certificate Count API Handling with deduplication
const fetchCertificateCount = async (requestBody, dispatch, getState) => {
    // Same "same-request dedup + stale-response guard" pattern as the search call above: only
    // reuse an in-flight count call for the identical request, and drop a count response if a
    // different, newer count request has been issued since - otherwise a slow, stale count (e.g.
    // for the landing page's unfiltered fetch) can overwrite a newer search's correct total.
    //
    // Staleness is judged by a monotonically increasing sequence number, not the request key:
    // the key gets reset to null once a request settles (see the finally block below), so two
    // *different* requests separated in time by another one can end up with an identical
    // serialized body. Comparing keys alone would let a late, stale response for the first slip
    // through as if it were the later, current one. The sequence number is never reused, so that
    // aliasing can't happen.
    const requestKey = JSON.stringify(requestBody);
    const identicalRequestInFlight = certificateCountRequestKey === requestKey;
    if (!identicalRequestInFlight) {
        certificateCountSequence += 1;
    }
    const mySequence = certificateCountSequence;
    certificateCountRequestKey = requestKey;
    certificateCountPromise = deduplicateRequest(
        identicalRequestInFlight ? certificateCountPromise : null,
        async () => {
            try {
                const res = await axios.post(
                    buildApiV3Url(`certificates/search-count`),
                    requestBody,
                    {
                        withCredentials: true,
                    }
                );

                if (certificateCountSequence !== mySequence) return null;

                const { page } = getState().certificate;
                const pageData = { ...page.paginate };
                pageData.totalRecords = res?.data;
                pageData.indeterminate = false;

                dispatch(
                    setPage({
                        data: page.data,
                        paginate: pageData,
                    })
                );
                return res;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error fetching certificate count:", error);
                return null;
            }
        }
    );
    const thisCountPromise = certificateCountPromise;

    try {
        return await thisCountPromise;
    } finally {
        if (certificateCountPromise === thisCountPromise) {
            certificateCountPromise = null;
            certificateCountRequestKey = null;
        }
    }
};

const hasFilterValidationErrors = certificateFiltersState =>
    certificateFiltersState.certificateIdError.length > 0 ||
    certificateFiltersState.customerCodeError.length > 0;

const updateNoSearchState = (dispatch, certificateFiltersState, searchVal) => {
    dispatch(setNoSearch(isFiltersEmpty(certificateFiltersState) && searchVal === ""));
};

// Runs (or reuses, if identical) the search API call and applies its result to the page - unless
// a newer search has been issued since, in which case the response is discarded. Returns the
// shared in-flight promise so the caller can await the same settled result the dedup logic used.
//
// Staleness is judged by a monotonically increasing sequence number, not the request key: the
// key gets reset to null once a request settles (see the finally block in the caller), so two
// *different* requests separated in time by another one can end up with an identical serialized
// body. Comparing keys alone would let a late, stale response for the first slip through as if
// it were the later, current one. The sequence number is never reused, so that aliasing can't
// happen.
const runCertificateSearch = (requestBody, searchContext) => {
    const requestKey = JSON.stringify(requestBody);
    const identicalRequestInFlight = certificateSearchRequestKey === requestKey;
    if (!identicalRequestInFlight) {
        certificateSearchSequence += 1;
    }
    const mySequence = certificateSearchSequence;
    certificateSearchRequestKey = requestKey;

    certificateSearchPromise = deduplicateRequest(
        identicalRequestInFlight ? certificateSearchPromise : null,
        async () => {
            const { dispatch, getState, onLanding, pageSize, pageNumber, paginateDetails } =
                searchContext;
            const apiResponse = await fetchCertificatesData(
                requestBody,
                dispatch,
                onLanding,
                pageSize
            );

            if (!apiResponse) return null;

            // Discard a stale/superseded response: a newer search may have been issued while
            // this one was in flight (e.g. the landing page's unfiltered fetch resolving after
            // a search typed on top of it). Applying it now would wipe out the newer, correct
            // results with older ones, regardless of which request happens to resolve last.
            if (certificateSearchSequence !== mySequence) return null;

            const response = transformExemptionData(apiResponse, false, false, [
                "expectedTaxCodeName",
                "taxCodeName",
            ]);
            handlePagination(
                apiResponse,
                getState().certificate.page.paginate,
                dispatch,
                pageSize,
                pageNumber,
                paginateDetails,
                response
            );

            // Set the certificate IDs
            const certificateIds = response.data.value.map(element => element.certificateId);
            dispatch(setCertificateIds(certificateIds));

            return { requestBody, response };
        }
    );

    return certificateSearchPromise;
};

// Clears the deferred search-while-loading value and fetches the certificate count for a
// successfully-applied search result.
const finalizeSuccessfulSearch = async (result, formState, dispatch, getState) => {
    dispatch(setLoading(false));

    if (formState.nameOrCodeWhileLoading !== "") {
        await dispatch(
            setFormState({
                ...formState,
                nameOrCodeWhileLoading: "",
            })
        );
    }

    dispatch(setIsLoadingCertificateCount(true));
    dispatch(incrementCertificateCountAPICompleted());
    await fetchCertificateCount(result.requestBody, dispatch, getState);
    dispatch(decrementCertificateCountAPICompleted());
    dispatch(setIsLoadingCertificateCount(false));
};

const fetchCertificatesSearchAPI =
    (
        onLanding,
        paginateDetails,
        pageSize,
        pageNumber = 1,
        skipLoadingCheck = false,
        searchType = 0
    ) =>
    async (dispatch, getState) => {
        const {
            formState,
            certificateFiltersState,
            sortColumn,
            certificateStatuses,
            taxTypeDetails,
        } = getState().certificate;
        const { rowsPerPage } = getState().session;

        if (hasFilterValidationErrors(certificateFiltersState)) {
            showToast(
                "error",
                "Please fix the highlighted filter issues before submitting your search."
            );
            return;
        }

        pageSize = pageSize || rowsPerPage;
        const searchVal = getSearchVal(formState, certificateFiltersState);

        // Handle loading state early and return if skip
        if (handleLoadingState(dispatch, formState, searchVal, skipLoadingCheck)) return;

        await reloadCertificateStatuses(dispatch);
        const statuses = getStoredItem("certificateStatuses") || certificateStatuses;
        const exemptionStatuses = getExemptionStatuses(certificateFiltersState, statuses);

        updateNoSearchState(dispatch, certificateFiltersState, searchVal);

        const requestBody = createRequestBody({
            searchVal,
            certificateFiltersState,
            pageSize,
            pageNumber,
            sortColumn,
            exemptionStatuses,
            searchType,
            taxTypeDetails,
        });

        // Use deduplication to prevent duplicate concurrent calls for the *same* search (e.g. a
        // React StrictMode double-invoke of the mount effect). Keyed on the request body so a
        // distinct search (e.g. the user typing a term while the landing page's unfiltered fetch
        // is still in flight) always gets its own call, instead of silently reusing - and
        // discarding the results of - whatever different search happened to be in flight.
        const thisSearchPromise = runCertificateSearch(requestBody, {
            dispatch,
            getState,
            onLanding,
            pageSize,
            pageNumber,
            paginateDetails,
        });

        try {
            const result = await thisSearchPromise;
            if (!result) return;

            await finalizeSuccessfulSearch(result, formState, dispatch, getState);
        } finally {
            // Only clear the module-level trackers if a newer request hasn't already replaced
            // them, so this settling request can't clobber a different one still in flight.
            if (certificateSearchPromise === thisSearchPromise) {
                certificateSearchPromise = null;
                certificateSearchRequestKey = null;
            }
        }
    };

export default fetchCertificatesSearchAPI;
